import React from "react";
import { Link } from "react-router-dom";
import useSettings from "../hooks/useSettings";

/**
 * Logo — DeStoel.com brand mark.
 *
 * Reads `logo_url` from /api/settings (cached) so the admin can replace the logo
 * without a redeploy. Falls back to /logo.png while settings load or when the
 * API is unreachable.
 *
 * Props:
 *   logoSrc   string  — explicit override (skips the settings hook)
 *   size      "header" | "footer"  — controls height
 *   linked    boolean  — wrap in <Link to="/">. Defaults true.
 *   className string   — additional classes
 *   alt       string   — accessible name (default "DeStoel.com")
 */
export default function Logo({
  logoSrc,
  size = "header",
  linked = true,
  className = "",
  alt = "DeStoel.com",
}) {
  const settings = useSettings();
  const src = logoSrc || settings?.logo_url || "/logo.png";

  // Container height controls visual scale. `object-contain` preserves the
  // transparent margins around the chair silhouette, so the terracotta frame
  // stays prominent at every size.
  const heightClass = size === "footer" ? "h-9 md:h-10" : "h-10 md:h-12";

  const img = (
    <img
      src={src}
      alt={alt}
      data-testid={`brand-logo-${size}`}
      className={`${heightClass} w-auto object-contain select-none ${className}`}
      draggable={false}
    />
  );
  if (!linked) return img;
  return (
    <Link
      to="/"
      data-testid={`brand-logo-link-${size}`}
      aria-label={alt}
      className="inline-flex items-center"
    >
      {img}
    </Link>
  );
}
